/**
 * Request Cache and Deduplication Utility
 * Prevents multiple simultaneous requests to the same endpoint
 * Implements stale-while-revalidate pattern to eliminate flickering
 */

class RequestCache {
  constructor() {
    // Store in-flight requests to prevent duplicates
    this.inFlightRequests = new Map();
    
    // Store cached responses with timestamps
    this.cache = new Map();
    
    // Default cache duration (5 seconds - data is fresh)
    this.cacheDuration = 5000;
    
    // Stale duration (30 seconds - data is stale but usable)
    this.staleDuration = 30000;
  }

  /**
   * Generate cache key from URL and options
   */
  getCacheKey(url, options = {}) {
    const method = options.method || 'GET';
    const body = options.body || '';
    return `${method}:${url}:${body}`;
  }

  /**
   * Check if cached data is fresh
   */
  isFresh(timestamp) {
    return Date.now() - timestamp < this.cacheDuration;
  }

  /**
   * Check if cached data is stale (but still usable)
   */
  isStale(timestamp) {
    return Date.now() - timestamp < this.staleDuration;
  }

  /**
   * Get cached response if available
   */
  getCached(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (this.isFresh(cached.timestamp)) {
      return { data: cached.data, source: 'fresh' };
    }

    if (this.isStale(cached.timestamp)) {
      return { data: cached.data, source: 'stale' };
    }

    // Data is too old, remove from cache
    this.cache.delete(key);
    return null;
  }

  /**
   * Store response in cache
   */
  setCached(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  /**
   * Deduplicated fetch with caching
   * If same request is in-flight, returns the existing promise
   * If cached data exists, returns it immediately and revalidates in background
   */
  async fetch(url, options = {}, fetchFn) {
    const key = this.getCacheKey(url, options);

    // Check if request is already in-flight
    if (this.inFlightRequests.has(key)) {
      console.log(`[RequestCache] Reusing in-flight request: ${url}`);
      return this.inFlightRequests.get(key);
    }

    // Check cache for stale-while-revalidate
    const cached = this.getCached(key);
    
    if (cached) {
      if (cached.source === 'fresh') {
        console.log(`[RequestCache] Returning fresh cached data: ${url}`);
        return cached.data;
      }
      
      if (cached.source === 'stale') {
        console.log(`[RequestCache] Returning stale data and revalidating: ${url}`);
        // Return stale data immediately, revalidate in background
        this.revalidate(key, url, options, fetchFn);
        return cached.data;
      }
    }

    // No cache, make the request
    const promise = this.makeRequest(key, url, options, fetchFn);
    this.inFlightRequests.set(key, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      this.inFlightRequests.delete(key);
    }
  }

  /**
   * Make the actual request
   */
  async makeRequest(key, url, options, fetchFn) {
    try {
      const response = await fetchFn(url, options);
      
      // Only cache successful responses
      if (response.ok) {
        // Read response as text first, then parse to avoid clone issues
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          // If not JSON, return raw text
          data = text;
        }
        this.setCached(key, data);
        return data;
      }
      
      return response;
    } catch (error) {
      // If error and we have stale cache, return stale data
      const cached = this.cache.get(key);
      if (cached && this.isStale(cached.timestamp)) {
        console.log(`[RequestCache] Error occurred, returning stale cache: ${url}`);
        return cached.data;
      }
      throw error;
    }
  }

  /**
   * Revalidate stale cache in background
   */
  async revalidate(key, url, options, fetchFn) {
    // Don't wait for this, just update cache when complete
    this.makeRequest(key, url, options, fetchFn).catch(error => {
      console.error(`[RequestCache] Background revalidation failed: ${url}`, error);
    });
  }

  /**
   * Clear cache for specific key or all
   */
  clear(key = null) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clear all caches
   */
  clearAll() {
    this.cache.clear();
    this.inFlightRequests.clear();
  }
}

// Singleton instance
const requestCache = new RequestCache();

export default requestCache;
